/*
 * ChecklistDetail.jsx
 * -------------------
 * Individual checklist page showing all items.
 * Supports checking off items with undo toast, adding new items,
 * editing item text inline, setting due dates and priority,
 * drag and drop reordering, item notes, recurring settings display,
 * streak badge, progress bar, countdown badge, confetti on completion,
 * and keyboard shortcuts.
 * Works for both authenticated and guest users.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useToast } from "../App";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ChecklistItem from "../components/ChecklistItem";
import ProgressBar from "../components/ProgressBar";
import StreakBadge from "../components/StreakBadge";
import CountdownBadge from "../components/CountdownBadge";
import CategoryTag from "../components/CategoryTag";
import Confetti from "../components/Confetti";

// ============================================
// SORTABLE ITEM WRAPPER
// ============================================
function SortableItem({ item, onCheck, onUpdate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto"
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ChecklistItem
        item={item}
        onCheck={onCheck}
        onUpdate={onUpdate}
        onDelete={onDelete}
        dragHandleListeners={listeners}
        dragHandleAttributes={attributes}
        isDragging={isDragging}
      />
    </div>
  );
}

// ============================================
// ADD ITEM INPUT
// Sticky at bottom of item list
// ============================================
function AddItemInput({ onAdd }) {
  const [text, setText] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("medium");
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef(null);

  function handleSubmit() {
    if (!text.trim()) return;
    onAdd({
      id: crypto.randomUUID(),
      text: text.trim(),
      checked: false,
      priority,
      dueDate: dueDate || null,
      notes: null,
      skippedOn: null
    });
    setText("");
    setDueDate("");
    setPriority("medium");
    setExpanded(false);
    inputRef.current?.focus();
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") handleSubmit();
    if (e.key === "Escape") {
      setText("");
      setExpanded(false);
    }
  }

  const priorityOptions = [
    { value: "high",   label: "High",   color: "var(--priority-high)" },
    { value: "medium", label: "Medium", color: "var(--priority-medium)" },
    { value: "low",    label: "Low",    color: "var(--priority-low)" }
  ];

  return (
    <div style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-md)",
      overflow: "hidden",
      marginTop: "var(--space-3)"
    }}>

      {/* Main input row */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)"
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: "var(--text-muted)", flexShrink: 0 }}>
          <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setExpanded(true)}
          placeholder="Add an item... (Enter to save)"
          style={{
            flex: 1,
            background: "none",
            border: "none",
            outline: "none",
            fontFamily: "var(--font-body)",
            fontSize: "15px",
            color: "var(--text-primary)",
            caretColor: "var(--accent-green)"
          }}
        />
        {text && (
          <button
            onClick={handleSubmit}
            style={{
              background: "var(--accent-green)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              padding: "var(--space-1) var(--space-3)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: "13px",
              fontWeight: 600,
              color: "white",
              flexShrink: 0
            }}
          >
            Add
          </button>
        )}
      </div>

      {/* Expanded options */}
      {expanded && (
        <div style={{
          borderTop: "1px solid var(--border)",
          padding: "var(--space-3) var(--space-4)",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-4)",
          flexWrap: "wrap",
          background: "var(--bg-surface-2)"
        }}>

          {/* Due date */}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <span style={{ fontFamily: "var(--font-body)", fontSize: "12px", color: "var(--text-muted)" }}>Due</span>
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "13px",
                color: "var(--text-primary)",
                background: "var(--bg-surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "2px var(--space-2)",
                outline: "none",
                colorScheme: "dark"
              }}
            />
          </div>

          {/* Priority */}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <span style={{ fontFamily: "var(--font-body)", fontSize: "12px", color: "var(--text-muted)" }}>Priority</span>
            <div style={{ display: "flex", gap: "var(--space-1)" }}>
              {priorityOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPriority(opt.value)}
                  style={{
                    background: priority === opt.value ? opt.color : "none",
                    border: "1px solid " + (priority === opt.value ? opt.color : "var(--border)"),
                    borderRadius: "var(--radius-sm)",
                    padding: "2px var(--space-2)",
                    cursor: "pointer",
                    fontFamily: "var(--font-body)",
                    fontSize: "12px",
                    fontWeight: priority === opt.value ? 600 : 400,
                    color: priority === opt.value ? "white" : "var(--text-muted)",
                    transition: "all var(--transition-fast)"
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => { setText(""); setExpanded(false); }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: "12px",
              color: "var(--text-muted)",
              marginLeft: "auto"
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// EDIT CHECKLIST MODAL
// ============================================
function EditModal({ checklist, onClose, onSave }) {
  const [title, setTitle] = useState(checklist.title);
  const [category, setCategory] = useState(checklist.category || "Personal");
  const [recurring, setRecurring] = useState(checklist.recurring || false);
  const [recurringInterval, setRecurringInterval] = useState(checklist.recurringInterval || "weekly");
  const [targetDate, setTargetDate] = useState(checklist.targetDate || "");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        category,
        recurring,
        recurringInterval: recurring ? recurringInterval : null,
        targetDate: targetDate || null
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const INTERVALS = [
    { value: "daily",     label: "Daily" },
    { value: "weekly",    label: "Weekly" },
    { value: "monthly",   label: "Monthly" },
    { value: "monday",    label: "Every Monday" },
    { value: "wednesday", label: "Every Wednesday" },
    { value: "friday",    label: "Every Friday" },
    { value: "sunday",    label: "Every Sunday" },
  ];

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1200, animation: "fadeIn 150ms ease"
      }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(480px, calc(100vw - 32px))",
        background: "var(--bg-surface)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-lg)",
        zIndex: 1201, overflow: "hidden",
        animation: "fadeIn 200ms ease"
      }}>
        <div style={{ height: "4px", background: "linear-gradient(90deg, var(--accent-green), var(--accent-gold))" }} />
        <div style={{ padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>

          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "22px", color: "var(--text-primary)", margin: 0 }}>
            Edit Checklist
          </h2>

          {/* Title */}
          <input
            autoFocus
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSave()}
            style={{
              width: "100%", fontFamily: "var(--font-body)", fontSize: "15px",
              color: "var(--text-primary)", background: "var(--bg-surface-2)",
              border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
              padding: "var(--space-3) var(--space-4)", outline: "none",
              boxSizing: "border-box", transition: "border-color var(--transition-fast)"
            }}
            onFocus={e => e.target.style.borderColor = "var(--accent-green)"}
            onBlur={e => e.target.style.borderColor = "var(--border)"}
          />

          {/* Category */}
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            {["Personal", "Work", "Home", "Shopping"].map(cat => (
              <CategoryTag key={cat} category={cat} size="md" active={category === cat} onClick={() => setCategory(cat)} />
            ))}
          </div>

          {/* Target date */}
          <input
            type="date"
            value={targetDate}
            onChange={e => setTargetDate(e.target.value)}
            style={{
              fontFamily: "var(--font-body)", fontSize: "14px",
              color: "var(--text-primary)", background: "var(--bg-surface-2)",
              border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
              padding: "var(--space-2) var(--space-3)", outline: "none", colorScheme: "dark"
            }}
          />

          {/* Recurring */}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
            <button
              onClick={() => setRecurring(prev => !prev)}
              style={{
                width: "44px", height: "24px", borderRadius: "100px",
                background: recurring ? "var(--accent-green)" : "var(--border-strong)",
                border: "none", cursor: "pointer", position: "relative",
                transition: "background var(--transition-fast)"
              }}
            >
              <div style={{
                width: "18px", height: "18px", borderRadius: "50%",
                background: "white", position: "absolute", top: "3px",
                left: recurring ? "23px" : "3px",
                transition: "left var(--transition-fast)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
              }} />
            </button>
            <span style={{ fontFamily: "var(--font-body)", fontSize: "14px", color: "var(--text-secondary)" }}>
              Recurring
            </span>
            {recurring && (
              <select
                value={recurringInterval}
                onChange={e => setRecurringInterval(e.target.value)}
                style={{
                  fontFamily: "var(--font-body)", fontSize: "13px",
                  color: "var(--text-primary)", background: "var(--bg-surface-2)",
                  border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
                  padding: "var(--space-1) var(--space-2)", outline: "none"
                }}
              >
                {INTERVALS.map(i => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
            )}
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: "var(--space-3)", justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{
              background: "none", border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-5)",
              cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "14px",
              color: "var(--text-secondary)"
            }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={!title.trim() || saving} style={{
              background: "var(--accent-green)", border: "none",
              borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-5)",
              cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "14px",
              fontWeight: 600, color: "white", opacity: saving ? 0.7 : 1
            }}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================
// KEYBOARD SHORTCUTS
// ============================================
function useKeyboardShortcuts({ onNew, onBack }) {
  useEffect(() => {
    function handleKey(e) {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || tag === "select";
      if (isTyping) return;

      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        onNew();
      }
      if (e.key === "Backspace" || e.key === "Escape") {
        onBack();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onNew, onBack]);
}

// ============================================
// MAIN EXPORT
// ============================================
export default function ChecklistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();
  const { showToast } = useToast();

  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [streakMilestone, setStreakMilestone] = useState(null);
  const addInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ============================================
  // KEYBOARD SHORTCUTS
  // ============================================
  useKeyboardShortcuts({
    onNew: () => addInputRef.current?.focus(),
    onBack: () => navigate("/checklists")
  });

  // ============================================
  // FETCH CHECKLIST
  // ============================================
  const fetchChecklist = useCallback(async () => {
    try {
      const headers = { "Content-Type": "application/json" };
      if (isGuest) headers["x-guest-id"] = user?.id || "";

      const res = await fetch("/api/checklists", { headers });
      const data = await res.json();
      const found = data.find(c => c.id === id);

      if (!found) {
        navigate("/checklists");
        return;
      }
      setChecklist(found);
    } catch (err) {
      console.error("Failed to fetch checklist", err);
    } finally {
      setLoading(false);
    }
  }, [id, user, isGuest, navigate]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  // ============================================
  // SAVE CHECKLIST TO API
  // ============================================
  const saveChecklist = useCallback(async (updated) => {
    setSaving(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (isGuest) headers["x-guest-id"] = user?.id || "";

      const res = await fetch("/api/checklists", {
        method: "PUT",
        headers,
        body: JSON.stringify({
          id: updated.id,
          title: updated.title,
          items: updated.items,
          category: updated.category,
          recurring: updated.recurring,
          recurringInterval: updated.recurringInterval,
          targetDate: updated.targetDate
        })
      });

      const data = await res.json();

      // Check for streak milestone
      if (data.streakMilestone) {
        setStreakMilestone(data.streakMilestone);
        setTimeout(() => setStreakMilestone(null), 5000);
      }

      // Check if just completed
      const allDone = updated.items.length > 0 && updated.items.every(i => i.checked);
      if (allDone) setShowConfetti(true);

    } catch (err) {
      console.error("Failed to save checklist", err);
    } finally {
      setSaving(false);
    }
  }, [user, isGuest]);

  // ============================================
  // CHECK OFF ITEM
  // ============================================
  function handleCheckItem(item) {
    if (!checklist) return;

    const originalItems = checklist.items;

    const updatedItems = checklist.items.map(i =>
      i.id === item.id ? { ...i, checked: true } : i
    );

    const updated = { ...checklist, items: updatedItems };
    setChecklist(updated);

    showToast("Item checked off", () => {
      setChecklist({ ...checklist, items: originalItems });
      saveChecklist({ ...checklist, items: originalItems });
    });

    saveChecklist(updated);
  }

  // ============================================
  // UPDATE ITEM (text, notes, priority, due date)
  // ============================================
  function handleUpdateItem(updatedItem) {
    if (!checklist) return;

    const updatedItems = checklist.items.map(i =>
      i.id === updatedItem.id ? updatedItem : i
    );

    const updated = { ...checklist, items: updatedItems };
    setChecklist(updated);
    saveChecklist(updated);
  }

  // ============================================
  // DELETE ITEM
  // ============================================
  function handleDeleteItem(itemId) {
    if (!checklist) return;

    const updatedItems = checklist.items.filter(i => i.id !== itemId);
    const updated = { ...checklist, items: updatedItems };
    setChecklist(updated);
    saveChecklist(updated);

    showToast("Item deleted");
  }

  // ============================================
  // ADD NEW ITEM
  // ============================================
  function handleAddItem(newItem) {
    if (!checklist) return;

    const updated = { ...checklist, items: [...checklist.items, newItem] };
    setChecklist(updated);
    saveChecklist(updated);
  }

  // ============================================
  // DRAG AND DROP REORDER
  // ============================================
  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = checklist.items.findIndex(i => i.id === active.id);
    const newIndex = checklist.items.findIndex(i => i.id === over.id);
    const reordered = arrayMove(checklist.items, oldIndex, newIndex);

    const updated = { ...checklist, items: reordered };
    setChecklist(updated);
    saveChecklist(updated);
  }

  // ============================================
  // EDIT CHECKLIST METADATA
  // ============================================
  async function handleEditSave(updates) {
    if (!checklist) return;

    const updated = { ...checklist, ...updates };
    setChecklist(updated);
    await saveChecklist(updated);
  }

  // ============================================
  // DELETE CHECKLIST
  // ============================================
  async function handleDeleteChecklist() {
    if (!window.confirm("Delete this checklist? This cannot be undone.")) return;

    try {
      const headers = { "Content-Type": "application/json" };
      if (isGuest) headers["x-guest-id"] = user?.id || "";

      await fetch("/api/checklists?id=" + checklist.id, {
        method: "DELETE",
        headers
      });

      navigate("/checklists");
    } catch (err) {
      console.error("Failed to delete checklist", err);
    }
  }

  // ============================================
  // DERIVED DATA
  // ============================================
  if (loading) {
    return (
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "center", minHeight: "60vh"
      }}>
        <p style={{ fontFamily: "var(--font-body)", color: "var(--text-muted)" }}>
          Loading...
        </p>
      </div>
    );
  }

  if (!checklist) return null;

  const total = checklist.items.length;
  const completed = checklist.items.filter(i => i.checked).length;
  const activeItems = checklist.items.filter(i => !i.checked);
  const completedItems = checklist.items.filter(i => i.checked);

  return (
    <div style={{
      maxWidth: "700px",
      margin: "0 auto",
      padding: "var(--space-6) var(--space-4)",
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-5)"
    }}>

      {/* Confetti */}
      {showConfetti && (
        <Confetti onComplete={() => setShowConfetti(false)} />
      )}

      {/* Back button */}
      <button
        onClick={() => navigate("/checklists")}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: "var(--font-body)",
          fontSize: "14px",
          color: "var(--text-muted)",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          padding: 0,
          alignSelf: "flex-start",
          transition: "color var(--transition-fast)"
        }}
        onMouseEnter={e => e.currentTarget.style.color = "var(--accent-green)"}
        onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        All Checklists
      </button>

      {/* Header */}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "var(--space-4)"
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              fontFamily: "var(--font-display)",
              fontWeight: 900,
              fontSize: "clamp(24px, 5vw, 36px)",
              color: "var(--text-primary)",
              margin: "0 0 var(--space-2)",
              letterSpacing: "-1px",
              wordBreak: "break-word"
            }}>
              {checklist.title}
            </h1>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              flexWrap: "wrap"
            }}>
              <CategoryTag category={checklist.category} size="sm" />
              {checklist.streak > 0 && (
                <StreakBadge
                  streak={checklist.streak}
                  size="sm"
                  showLabel={true}
                  justReached={streakMilestone !== null}
                />
              )}
              {checklist.targetDate && (
                <CountdownBadge targetDate={checklist.targetDate} size="sm" />
              )}
              {checklist.recurring && (
                <span style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  display: "flex",
                  alignItems: "center",
                  gap: "3px"
                }}>
                  🔄 {checklist.recurringInterval}
                </span>
              )}
              {saving && (
                <span style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "12px",
                  color: "var(--text-muted)"
                }}>
                  Saving...
                </span>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: "var(--space-2)", flexShrink: 0 }}>
            <button
              onClick={() => setShowEdit(true)}
              style={{
                background: "none",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-2) var(--space-3)",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: "13px",
                color: "var(--text-secondary)",
                transition: "background var(--transition-fast)"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--bg-surface-2)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              Edit
            </button>
            <button
              onClick={handleDeleteChecklist}
              style={{
                background: "none",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-2) var(--space-3)",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: "13px",
                color: "var(--status-overdue)",
                transition: "background var(--transition-fast)"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--status-overdue-muted)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              Delete
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {total > 0 && (
          <ProgressBar total={total} completed={completed} height={8} />
        )}
      </div>

      {/* Keyboard shortcut hints */}
      <div style={{
        display: "flex",
        gap: "var(--space-4)",
        flexWrap: "wrap"
      }}>
        {[
          { key: "N", label: "New item" },
          { key: "Esc", label: "Back" }
        ].map(({ key, label }) => (
          <span key={key} style={{
            fontFamily: "var(--font-body)",
            fontSize: "11px",
            color: "var(--text-muted)",
            display: "flex",
            alignItems: "center",
            gap: "4px"
          }}>
            <kbd style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border)",
              borderRadius: "4px",
              padding: "1px 5px",
              fontSize: "11px",
              fontFamily: "var(--font-body)"
            }}>
              {key}
            </kbd>
            {label}
          </span>
        ))}
      </div>

      {/* Active items */}
      {activeItems.length > 0 && (
        <div style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          overflow: "hidden"
        }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeItems.map(i => i.id)}
              strategy={verticalListSortingStrategy}
            >
              {activeItems.map(item => (
                <SortableItem
                  key={item.id}
                  item={item}
                  onCheck={handleCheckItem}
                  onUpdate={handleUpdateItem}
                  onDelete={handleDeleteItem}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Add item input */}
      <div ref={addInputRef}>
        <AddItemInput onAdd={handleAddItem} />
      </div>

      {/* Completed items */}
      {completedItems.length > 0 && (
        <div>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            fontWeight: 600,
            color: "var(--text-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            margin: "0 0 var(--space-2)"
          }}>
            Completed ({completedItems.length})
          </p>
          <div style={{
            background: "var(--bg-surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)",
            overflow: "hidden",
            opacity: 0.7
          }}>
            {completedItems.map(item => (
              <ChecklistItem
                key={item.id}
                item={item}
                onCheck={() => {}}
                onUpdate={handleUpdateItem}
                onDelete={handleDeleteItem}
                readOnly={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {total === 0 && (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "var(--space-7)",
          gap: "var(--space-3)",
          textAlign: "center"
        }}>
          <span style={{ fontSize: "48px" }}>✏️</span>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "15px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            No items yet. Add your first item above or press N.
          </p>
        </div>
      )}

      {/* Edit modal */}
      {showEdit && (
        <EditModal
          checklist={checklist}
          onClose={() => setShowEdit(false)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
}